import { useEffect, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { UI, LANG } from "../data/content";
import { Reveal, HeroBand, SectionHead, ContactModule, usePageMeta } from "../components/kit";

export default function Sources() {
  const T = UI.pages.sources;
  usePageMeta(T.metaTitle, T.standfirst);
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get("/funding/sources-meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const name = (p) => (LANG === "el" && p.name_el ? p.name_el : p.name_en);
  const auth = (p) => (LANG === "el" && p.authority_el ? p.authority_el : p.authority_en);

  return (
    <div data-testid="sources-page">
      <HeroBand image="facade" eyebrow={T.eyebrow} title={T.title} standfirst={T.standfirst} compact testId="sources-hero" />

      <section className="k-section !py-14">
        <div className="k-container">
          <SectionHead eyebrow={T.eyebrow} title={T.principlesTitle} />
          <div className="grid md:grid-cols-3 gap-8">
            {T.principles.map(([t, b], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="border-t-2 border-[var(--accent)] pt-5">
                  <h3 className="text-base font-semibold mb-3">{t}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="k-section !py-14 bg-paper-alt" data-testid="sources-acts">
        <div className="k-container">
          <h2 className="k-h2 mb-3">{T.actsTitle}</h2>
          <p className="text-ink-muted mb-8 max-w-3xl">{T.actsLead}</p>
          <div className="max-w-4xl divide-y divide-[var(--line)] border-t border-b border-line">
            {T.acts.map(([act, what, href]) => (
              <div key={act} className="py-4 grid sm:grid-cols-12 gap-2 sm:gap-6 items-baseline">
                <a href={href} target="_blank" rel="noopener noreferrer" className="sm:col-span-5 text-sm font-semibold text-brand underline decoration-[var(--accent)] underline-offset-4">{act}</a>
                <p className="sm:col-span-7 text-sm text-ink-muted">{what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="k-section !py-14" data-testid="sources-register">
        <div className="k-container">
          <h2 className="k-h2 mb-3">{T.registerTitle}</h2>
          <p className="text-ink-muted mb-8 max-w-3xl">{T.registerLead}</p>
          {meta && (
            <>
              <div className="overflow-x-auto">
                <table className="k-table" data-testid="sources-register-table">
                  <thead><tr><th>{T.colProgramme}</th><th>{T.colAuthority}</th><th>{T.colBasis}</th><th>{T.colSource}</th><th>{T.colVerified}</th></tr></thead>
                  <tbody>
                    {meta.programmes.map((p) => (
                      <tr key={p.slug}>
                        <td className="font-semibold">{name(p)}</td>
                        <td className="text-ink-muted">{auth(p)}</td>
                        <td className="text-ink-muted">{p.legal_basis}</td>
                        <td>{p.source_url ? <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="text-brand underline">{T.sourceLink}</a> : "—"}</td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>{p.verified_at ? fmtDate(p.verified_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-ink-muted mt-4" data-testid="sources-counts">
                {T.countsLine(meta.counts.programmes, meta.counts.calls, meta.counts.regions)}
              </p>
            </>
          )}
        </div>
      </section>

      <section className="k-section !py-14 bg-paper-alt" data-testid="sources-pipeline">
        <div className="k-container grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="k-h2 mb-4">{T.pipelineTitle}</h2>
            <p className="text-ink-muted leading-relaxed mb-6 max-w-xl">{T.pipelineBody}</p>
            <div className="space-y-3">
              {(meta?.pipelines || []).map((s) => (
                <div key={s.source} className="flex items-center gap-3 text-sm" data-testid={`pipeline-${s.source}`}>
                  <span className={`inline-block w-2 h-2 rounded-full ${s.last_status === "ok" ? "bg-emerald-600" : "bg-red-600"}`} />
                  <span className="font-semibold uppercase tracking-wider text-xs">{s.source === "diavgeia" ? "ΔΙΑΥΓΕΙΑ" : "SEDIA"}</span>
                  <span className="text-ink-muted">{s.last_status === "ok" ? T.statusOk : T.statusError} · {T.lastRun} {fmtDate(s.last_run)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="k-h2 mb-4">{T.correctionsTitle}</h2>
            <p className="text-ink-muted leading-relaxed max-w-xl" data-testid="sources-corrections">{T.correctionsBody}</p>
          </div>
        </div>
      </section>

      <section className="k-container py-16"><ContactModule /></section>
    </div>
  );
}
